import type { GraphicsResolutionLease, GraphicsStateOwner } from '@/graphics/GraphicsBackend'
import { GraphicsResolutionOwnerConflictError } from '@/graphics/GraphicsErrors'

interface ActiveResolutionLease {
  readonly owner: GraphicsStateOwner
  readonly previousPixelRatio: number
  currentPixelRatio: number
  disposed: boolean
}

function normalizePixelRatio(pixelRatio: number): number {
  if (!Number.isFinite(pixelRatio) || pixelRatio <= 0) {
    throw new Error(`非法 graphics pixelRatio: ${pixelRatio}`)
  }
  return pixelRatio
}

/** GraphicsBackend 共用的 surface resolution owner 状态机。 */
export class GraphicsResolutionOwner {
  private active: ActiveResolutionLease | null = null

  constructor(
    private readonly readPixelRatio: () => number,
    private readonly applyPixelRatio: (pixelRatio: number) => boolean
  ) {}

  get overridePixelRatio(): number | undefined {
    return this.active?.currentPixelRatio
  }

  acquire(owner: GraphicsStateOwner): GraphicsResolutionLease {
    if (this.active) throw new GraphicsResolutionOwnerConflictError()
    const state: ActiveResolutionLease = {
      owner,
      previousPixelRatio: this.readPixelRatio(),
      currentPixelRatio: this.readPixelRatio(),
      disposed: false,
    }
    this.active = state

    return {
      owner,
      get pixelRatio() {
        return state.currentPixelRatio
      },
      setPixelRatio: (pixelRatio) => {
        this.assertCurrent(state)
        const next = normalizePixelRatio(pixelRatio)
        if (next === state.currentPixelRatio) return false
        const changed = this.applyPixelRatio(next)
        state.currentPixelRatio = next
        return changed
      },
      dispose: () => {
        if (state.disposed) return
        state.disposed = true
        if (this.active !== state) return
        this.active = null
        this.applyPixelRatio(state.previousPixelRatio)
      },
    }
  }

  /** backend 销毁时丢弃租约，不再触碰已释放的 renderer/device。 */
  dispose(): void {
    if (!this.active) return
    this.active.disposed = true
    this.active = null
  }

  private assertCurrent(state: ActiveResolutionLease): void {
    if (state.disposed || this.active !== state) {
      throw new Error('GraphicsResolutionLease 已释放或失效')
    }
  }
}
